import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Shield, CreditCard, Crown, User, Home, Calendar, Video, FileText, Settings, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout, hasAIAccess, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const getPlanLabel = (plan) => {
    const labels = {
      'none': 'Sin Plan',
      'inicial': 'Inicial',
      'basic': 'Básico',
      'socio': 'Socio'
    };
    return labels[plan] || plan;
  };

  const getPlanColor = (plan) => {
    const colors = {
      'none': 'bg-gray-500/20 text-gray-400',
      'inicial': 'bg-blue-500/20 text-blue-400',
      'basic': 'bg-green-500/20 text-green-400',
      'socio': 'bg-purple-500/20 text-purple-400'
    };
    return colors[plan] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-white/10" data-testid="navbar">
      <div className="px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_virtualgolf-2/artifacts/si8l522g_IMG_1926.png" 
              alt="City Virtual Golf" 
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          {user ? (
            <>
              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
                <Link 
                  to="/dashboard" 
                  className={`text-sm xl:text-base transition-colors ${isActive('/dashboard') ? 'text-primary font-medium' : 'text-foreground/70 hover:text-foreground'}`}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/booking" 
                  className={`text-sm xl:text-base transition-colors ${isActive('/booking') ? 'text-primary font-medium' : 'text-foreground/70 hover:text-foreground'}`}
                >
                  Sacar Turno
                </Link>
                
                {hasAIAccess() ? (
                  <>
                    <Link 
                      to="/analysis" 
                      className={`text-sm xl:text-base flex items-center gap-1 transition-colors ${isActive('/analysis') ? 'text-primary font-medium' : 'text-foreground/70 hover:text-foreground'}`}
                    >
                      <Crown className="h-4 w-4 text-purple-400" />
                      Subir Material
                    </Link>
                    <Link 
                      to="/vault" 
                      className={`text-sm xl:text-base flex items-center gap-1 transition-colors ${isActive('/vault') ? 'text-primary font-medium' : 'text-foreground/70 hover:text-foreground'}`}
                    >
                      <Crown className="h-4 w-4 text-purple-400" />
                      Informes
                    </Link>
                  </>
                ) : (
                  <Link 
                    to="/plans" 
                    className="text-sm xl:text-base text-foreground/70 hover:text-foreground flex items-center gap-1"
                  >
                    Análisis IA
                    <span className="text-xs bg-purple-500/30 text-purple-300 px-1.5 py-0.5 rounded">PRO</span>
                  </Link>
                )}
                
                <Link 
                  to="/plans" 
                  className={`text-sm xl:text-base transition-colors ${isActive('/plans') ? 'text-primary font-medium' : 'text-foreground/70 hover:text-foreground'}`}
                >
                  Planes
                </Link>
                
                {isAdmin() && (
                  <Link 
                    to="/admin" 
                    className={`text-sm xl:text-base flex items-center gap-1 ${isActive('/admin') ? 'text-yellow-400' : 'text-yellow-500 hover:text-yellow-400'}`}
                  >
                    <Shield className="h-4 w-4" />
                    Admin
                  </Link>
                )}
              </div>

              {/* User Info - Desktop */}
              <div className="hidden lg:flex items-center gap-3">
                {/* Credits Badge */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/20 rounded-full">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <span className="font-bold text-primary">{user.credits || 0}</span>
                </div>

                {/* User Menu */}
                <div className="relative">
                  <button 
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    {user.profile_image ? (
                      <img src={user.profile_image} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary/30 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div className="text-left hidden xl:block">
                      <p className="text-sm font-medium truncate max-w-[120px]">{user.name}</p>
                      <p className={`text-xs px-1.5 py-0.5 rounded ${getPlanColor(user.plan)}`}>
                        {getPlanLabel(user.plan)}
                      </p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-background border border-white/10 rounded-lg shadow-xl py-2 z-50">
                      <Link 
                        to="/perfil" 
                        className="flex items-center gap-2 px-4 py-2 hover:bg-white/5 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User className="h-4 w-4" />
                        Mi Perfil
                      </Link>
                      <button 
                        onClick={handleLogout}
                        className="flex items-center gap-2 px-4 py-2 hover:bg-white/5 transition-colors w-full text-left text-red-400"
                      >
                        <LogOut className="h-4 w-4" />
                        Cerrar Sesión
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="lg:hidden flex items-center gap-2 p-2"
                data-testid="mobile-menu-button"
              >
                {/* Mobile Credits Badge */}
                <div className="flex items-center gap-1 px-2 py-1 bg-primary/20 rounded-full text-xs">
                  <CreditCard className="h-3 w-3 text-primary" />
                  <span className="font-bold text-primary">{user.credits || 0}</span>
                </div>
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </>
          ) : (
            /* Public Navigation */
            <div className="flex items-center space-x-3 sm:space-x-6">
              <Link to="/plans" className="hidden sm:block text-foreground/80 hover:text-foreground transition-colors text-sm">
                Planes
              </Link>
              <Link to="/login" className="text-foreground/80 hover:text-foreground transition-colors text-sm">
                Iniciar Sesión
              </Link>
              <Link 
                to="/register" 
                className="bg-primary text-primary-foreground px-4 sm:px-6 py-2 text-sm font-bold uppercase tracking-wider hover:bg-primary/90 transition-all"
              >
                Registrarse
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && user && (
        <div className="lg:hidden bg-black/95 border-t border-white/10 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
          {/* User Info Header */}
          <div className="px-4 py-4 border-b border-white/10 bg-white/5">
            <div className="flex items-center gap-3">
              {user.profile_image ? (
                <img src={user.profile_image} alt="" className="h-12 w-12 rounded-full object-cover" />
              ) : (
                <div className="h-12 w-12 rounded-full bg-primary/30 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{user.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${getPlanColor(user.plan)}`}>
                    {getPlanLabel(user.plan)}
                  </span>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-primary font-medium">{user.credits || 0} créditos</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="px-4 py-3 space-y-1">
            <Link 
              to="/dashboard" 
              className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isActive('/dashboard') ? 'bg-primary/20 text-primary' : 'hover:bg-white/5'}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <Home className="h-5 w-5" />
              <span>Dashboard</span>
            </Link>
            
            <Link 
              to="/booking" 
              className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isActive('/booking') ? 'bg-primary/20 text-primary' : 'hover:bg-white/5'}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <Calendar className="h-5 w-5" />
              <span>Sacar Turno</span>
            </Link>
            
            {hasAIAccess() ? (
              <>
                <Link 
                  to="/analysis" 
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isActive('/analysis') ? 'bg-primary/20 text-primary' : 'hover:bg-white/5'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Video className="h-5 w-5" />
                  <span>Subir Material</span>
                  <Crown className="h-4 w-4 text-purple-400 ml-auto" />
                </Link>
                <Link 
                  to="/vault" 
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isActive('/vault') ? 'bg-primary/20 text-primary' : 'hover:bg-white/5'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FileText className="h-5 w-5" />
                  <span>Informes de Entrenamiento</span>
                  <Crown className="h-4 w-4 text-purple-400 ml-auto" />
                </Link>
              </>
            ) : (
              <Link 
                to="/plans" 
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 text-purple-400"
                onClick={() => setIsMenuOpen(false)}
              >
                <Crown className="h-5 w-5" />
                <span>Análisis IA</span>
                <span className="text-xs bg-purple-500/30 px-2 py-0.5 rounded ml-auto">PRO</span>
              </Link>
            )}
            
            <Link 
              to="/plans" 
              className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isActive('/plans') ? 'bg-primary/20 text-primary' : 'hover:bg-white/5'}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <CreditCard className="h-5 w-5" />
              <span>Planes</span>
            </Link>
            
            <Link 
              to="/perfil" 
              className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isActive('/perfil') ? 'bg-primary/20 text-primary' : 'hover:bg-white/5'}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <Settings className="h-5 w-5" />
              <span>Mi Perfil</span>
            </Link>
            
            {isAdmin() && (
              <Link 
                to="/admin" 
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${isActive('/admin') ? 'bg-yellow-500/20 text-yellow-400' : 'text-yellow-500 hover:bg-yellow-500/10'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <Shield className="h-5 w-5" />
                <span>Panel Admin</span>
              </Link>
            )}
          </div>

          {/* Logout */}
          <div className="px-4 py-3 border-t border-white/10">
            <button 
              onClick={handleLogout} 
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-red-500/10 text-red-400 w-full"
            >
              <LogOut className="h-5 w-5" />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      )}

      {/* Backdrop for user menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </nav>
  );
};
